import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
    LocalNotifications,
    LocalNotificationSchema,
} from '@capacitor/local-notifications';
import { IncomingCall } from '../plugins/incoming-call.plugin';
import { TwilioVoice } from '../plugins/twilio-voice.plugin';

type NotificationScope = 'alerts' | 'calls';
const CALLS_CHANNEL_ID = 'connect-calls-v2';

@Injectable({
    providedIn: 'root',
})
export class NativeNotificationService {
    private listenersRegistered = false;
    private permissionRequest?: Promise<void>;
    private browserPermissionRequest?: Promise<boolean>;
    private browserPermissionListenersRegistered = false;
    private browserAudioContext?: AudioContext;
    private browserAudioUnlocked = false;
    private appStateListenersRegistered = false;

    constructor(private router: Router) {}

    isNativeAndroid(): boolean {
        return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    }

    async init(): Promise<void> {
        if (!this.isNativeAndroid()) {
            this.registerBrowserPermissionPrompt();
            return;
        }

        await this.createChannels();

        if (this.listenersRegistered) {
            return;
        }

        this.listenersRegistered = true;

        await LocalNotifications.addListener(
            'localNotificationActionPerformed',
            (event: any) => {
                const conversationId = this.parseConversationId(
                    event?.notification?.extra?.conversationId
                );
                const route = event?.notification?.extra?.route;

                if (conversationId) {
                    this.router
                        .navigate(['conversations'], {
                            queryParams: { id: conversationId },
                        })
                        .catch((error) => {
                            console.error(
                                'Failed to route notification to conversation:',
                                error
                            );
                        });
                    return;
                }

                if (route) {
                    this.router.navigate([route]).catch((error) => {
                        console.error('Failed to route notification action:', error);
                    });
                }
            }
        );

        if (!this.appStateListenersRegistered) {
            this.appStateListenersRegistered = true;

            await App.addListener('appStateChange', ({ isActive }) => {
                if (isActive) {
                    void this.requestStartupPermissions(true);
                }
            });

            await App.addListener('resume', () => {
                void this.requestStartupPermissions(true);
            });
        }
    }

    async requestStartupPermissions(force = false): Promise<void> {
        if (!this.isNativeAndroid()) {
            this.registerBrowserPermissionPrompt();
            return;
        }

        if (!force && this.permissionRequest) {
            await this.permissionRequest;
            return;
        }

        this.permissionRequest = this.performStartupPermissionRequest();

        try {
            await this.permissionRequest;
        } finally {
            this.permissionRequest = undefined;
        }
    }

    async notifyAlert(
        message: string,
        kind: 'sms' | 'email' | 'leads' | 'alert' = 'alert',
        conversationId?: string | number | null
    ): Promise<void> {
        if (!this.shouldShowSystemNotification()) {
            return;
        }

        const titleMap = {
            sms: 'New SMS',
            email: 'New Email',
            leads: 'New Leads',
            alert: 'New Alert',
        };

        if (!this.isNativeAndroid()) {
            this.playBrowserNotificationSound();
            await this.showBrowserNotification({
                title: titleMap[kind],
                body: message,
                kind,
                conversationId,
                route: conversationId ? '/conversations' : '/alerts',
            });
            return;
        }

        await this.scheduleNotification({
            id: this.generateId(),
            title: titleMap[kind],
            body: message,
            channelId: 'connect-alerts',
            extra: {
                scope: 'alerts',
                route: conversationId ? '/conversations' : '/alerts',
                kind,
                conversationId: conversationId || null,
            },
        });
    }

    async notifyIncomingCall(callData: any): Promise<void> {
        if (!this.isNativeAndroid() || !this.shouldShowSystemNotification()) {
            return;
        }

        try {
            await IncomingCall.showIncomingCall({
                callerName:
                    callData?.caller_name ||
                    callData?.customer_name ||
                    callData?.from_number ||
                    'Incoming caller',
                callerNumber: callData?.from_number || callData?.caller_number || '',
                conversationId: callData?.conversation_id ? `${callData.conversation_id}` : null,
                callSid: callData?.call_sid || null,
                dealerId: callData?.dealer_id ? `${callData.dealer_id}` : null,
                toNumber: callData?.to_number || null,
            });
            return;
        } catch (error) {
            console.error('Failed to show native incoming call UI:', error);
        }

        const caller =
            callData?.caller_name ||
            callData?.customer_name ||
            callData?.from_number ||
            'Incoming caller';

        const body = callData?.message || `Incoming call from ${caller}`;

        await this.scheduleNotification({
            id: this.generateId(),
            title: 'Incoming Call',
            body,
            channelId: CALLS_CHANNEL_ID,
            extra: {
                scope: 'calls',
                route: '/conversations',
                conversationId: callData?.conversation_id || null,
                callSid: callData?.call_sid || null,
            },
        });
    }

    async clearAlertNotifications(): Promise<void> {
        await this.clearNotificationsByScope('alerts');
    }

    async clearCallNotifications(): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        try {
            await IncomingCall.dismissIncomingCall();
        } catch (error) {
            console.error('Failed to dismiss native incoming call UI:', error);
        }
        await this.clearNotificationsByScope('calls');
    }

    shouldShowSystemNotification(): boolean {
        return this.isNativeAndroid() || this.isBrowserNotificationSupported();
    }

    async ensureCallPermissions(): Promise<boolean> {
        if (!this.isNativeAndroid()) {
            return true;
        }

        await this.ensureNotificationPermission();
        return this.ensureMicrophonePermission();
    }

    private async createChannels(): Promise<void> {
        try {
            await LocalNotifications.createChannel({
                id: 'connect-alerts',
                name: 'Connect Alerts',
                description: 'SMS, email, and alert notifications',
                importance: 5,
                visibility: 1,
            });

            await LocalNotifications.createChannel({
                id: CALLS_CHANNEL_ID,
                name: 'Connect Calls',
                description: 'Incoming call notifications',
                importance: 5,
                visibility: 1,
                sound: 'default',
            });
        } catch (error) {
            console.error('Failed to create Android notification channels:', error);
        }
    }

    private async performStartupPermissionRequest(): Promise<void> {
        await this.ensureNotificationPermission();
        await this.ensureMicrophonePermission();
    }

    private async ensureNotificationPermission(): Promise<void> {
        try {
            const permissionStatus = await LocalNotifications.checkPermissions();
            if (permissionStatus.display !== 'granted') {
                await LocalNotifications.requestPermissions();
            }
        } catch (error) {
            console.error('Failed to request notification permission:', error);
        }
    }

    private async ensureBrowserNotificationPermission(force = false): Promise<boolean> {
        if (!this.isBrowserNotificationSupported()) {
            return false;
        }

        if (Notification.permission === 'granted') {
            return true;
        }

        if (Notification.permission === 'denied') {
            return false;
        }

        if (!force && this.browserPermissionRequest) {
            return this.browserPermissionRequest;
        }

        this.browserPermissionRequest = Notification.requestPermission()
            .then((permission) => permission === 'granted')
            .catch((error) => {
                console.error('Failed to request browser notification permission:', error);
                return false;
            });

        try {
            return await this.browserPermissionRequest;
        } finally {
            this.browserPermissionRequest = undefined;
        }
    }

    private isBrowserNotificationSupported(): boolean {
        return typeof window !== 'undefined' && 'Notification' in window;
    }

    private async showBrowserNotification(options: {
        title: string;
        body: string;
        kind: 'sms' | 'email' | 'leads' | 'alert';
        conversationId?: string | number | null;
        route: string;
    }): Promise<void> {
        const granted = await this.ensureBrowserNotificationPermission();
        if (!granted) {
            return;
        }

        try {
            const notification = new Notification(options.title, {
                body: options.body || options.title,
                icon: '/assets/icon/icon-192x192.png',
                badge: '/assets/icon/icon-192x192.png',
                tag: this.browserNotificationTag(options.kind, options.conversationId, options.body),
            });

            notification.onclick = () => {
                window.focus();

                if (options.conversationId) {
                    this.router.navigate(['conversations'], {
                        queryParams: { id: options.conversationId },
                    }).catch((error) => {
                        console.error('Failed to route browser notification:', error);
                    });
                    notification.close();
                    return;
                }

                this.router.navigate([options.route]).catch((error) => {
                    console.error('Failed to route browser notification:', error);
                });
                notification.close();
            };
        } catch (error) {
            console.error('Failed to show browser notification:', error);
        }
    }

    private browserNotificationTag(
        kind: string,
        conversationId?: string | number | null,
        message?: string
    ): string {
        if (conversationId) {
            return `connect-${kind}-${conversationId}`;
        }

        return `connect-${kind}-${this.hashText(message || '')}`;
    }

    private hashText(value: string): string {
        let hash = 0;

        for (let i = 0; i < value.length; i++) {
            hash = ((hash << 5) - hash) + value.charCodeAt(i);
            hash |= 0;
        }

        return `${Math.abs(hash) || Date.now()}`;
    }

    private registerBrowserPermissionPrompt(): void {
        if (
            this.browserPermissionListenersRegistered ||
            !this.isBrowserNotificationSupported() ||
            Notification.permission !== 'default'
        ) {
            return;
        }

        this.browserPermissionListenersRegistered = true;

        const requestOnUserGesture = () => {
            window.removeEventListener('pointerdown', requestOnUserGesture);
            window.removeEventListener('keydown', requestOnUserGesture);

            this.unlockBrowserNotificationSound();

            this.ensureBrowserNotificationPermission(true).then((granted) => {
                if (!granted && Notification.permission === 'default') {
                    this.browserPermissionListenersRegistered = false;
                    this.registerBrowserPermissionPrompt();
                }
            });
        };

        window.addEventListener('pointerdown', requestOnUserGesture, { once: true });
        window.addEventListener('keydown', requestOnUserGesture, { once: true });
    }

    private unlockBrowserNotificationSound(): void {
        const audioContext = this.getBrowserAudioContext();
        if (!audioContext) {
            return;
        }

        audioContext.resume()
            .then(() => {
                this.browserAudioUnlocked = true;
            })
            .catch((error) => {
                console.error('Failed to unlock browser notification sound:', error);
            });
    }

    private playBrowserNotificationSound(): void {
        const audioContext = this.getBrowserAudioContext();
        if (!audioContext || !this.browserAudioUnlocked) {
            return;
        }

        try {
            const oscillator = audioContext.createOscillator();
            const gain = audioContext.createGain();
            const now = audioContext.currentTime;

            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(880, now);
            oscillator.frequency.setValueAtTime(660, now + 0.12);

            gain.gain.setValueAtTime(0.0001, now);
            gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);

            oscillator.connect(gain);
            gain.connect(audioContext.destination);
            oscillator.start(now);
            oscillator.stop(now + 0.34);
        } catch (error) {
            console.error('Failed to play browser notification sound:', error);
        }
    }

    private getBrowserAudioContext(): AudioContext | null {
        if (typeof window === 'undefined') {
            return null;
        }

        if (this.browserAudioContext) {
            return this.browserAudioContext;
        }

        const AudioContextConstructor =
            window.AudioContext || (window as any).webkitAudioContext;

        if (!AudioContextConstructor) {
            return null;
        }

        try {
            this.browserAudioContext = new AudioContextConstructor();
            return this.browserAudioContext;
        } catch (error) {
            console.error('Failed to initialize browser notification sound:', error);
            return null;
        }
    }

    private async ensureMicrophonePermission(): Promise<boolean> {
        if (this.isNativeAndroid()) {
            try {
                const result = await TwilioVoice.ensurePermissions();
                return result?.microphoneGranted === true;
            } catch (error) {
                console.error('Native microphone permission not granted:', error);
                return false;
            }
        }

        if (
            !navigator?.mediaDevices ||
            typeof navigator.mediaDevices.getUserMedia !== 'function'
        ) {
            return false;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach((track) => track.stop());
            return true;
        } catch (error) {
            console.error('Microphone permission not granted:', error);
            return false;
        }
    }

    private async scheduleNotification(
        notification: LocalNotificationSchema
    ): Promise<void> {
        try {
            await LocalNotifications.schedule({
                notifications: [
                    {
                        ...notification,
                        schedule: { at: new Date(Date.now() + 250) },
                    },
                ],
            });
        } catch (error) {
            console.error('Failed to schedule local notification:', error);
        }
    }

    private async clearNotificationsByScope(scope: NotificationScope): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        try {
            const delivered = await LocalNotifications.getDeliveredNotifications();
            const deliveredMatches = delivered.notifications.filter(
                (notification: any) => notification?.extra?.scope === scope
            );

            if (deliveredMatches.length > 0) {
                await LocalNotifications.removeDeliveredNotifications({
                    notifications: deliveredMatches,
                });
            }

            const pending = await LocalNotifications.getPending();
            const pendingMatches = pending.notifications
                .filter((notification: any) => notification?.extra?.scope === scope)
                .map((notification: any) => ({ id: notification.id }));

            if (pendingMatches.length > 0) {
                await LocalNotifications.cancel({
                    notifications: pendingMatches,
                });
            }
        } catch (error) {
            console.error(`Failed to clear ${scope} notifications:`, error);
        }
    }

    private generateId(): number {
        return Math.floor(Date.now() % 2147483647);
    }

    private parseConversationId(value: string | number | null | undefined): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }
}
